/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { cypressId } from '../../../../../utils/cypress-attribute';
import { Logger } from '../../../../../utils/logger';
import { UiIcon } from '../../../../common/icons/ui-icon';
import { ShowIf } from '../../../../common/show-if/show-if';
import { acceptedMimeTypes } from '../../../../common/upload-image-mimetypes';
import { useCodemirrorReferenceContext } from '../../../change-content-context/codemirror-reference-context';
import { UploadInput } from '../../../sidebar/upload-input';
import { useHandleUpload } from '../../hooks/use-handle-upload';
import { extractSelectedText } from './extract-selected-text';
import { Optional } from '@mrdrogdrog/optional';
import React, { Fragment, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { Icon, Upload as IconUpload } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

const logger = new Logger('Upload image button');

type UploadImageButtonProps = {
  onUploadImage: (file: File | undefined) => void;
  icon?: Icon;
  variant?: string;
  size?: string | number;
  supportedFormats?: string;
};

/**
 * Shows a button that uploads a chosen file to the backend and adds the link to the note.
 */
export const UploadImageButton: React.FC<UploadImageButtonProps> = ({
  onUploadImage,
  icon = IconUpload,
  variant = 'light',
  size,
  supportedFormats = acceptedMimeTypes,
}) => {
  const { t } = useTranslation();
  const clickRef = useRef<() => void>();
  const buttonClick = useCallback(() => {
    clickRef.current?.();
  }, []);

  const [codeMirror] = useCodemirrorReferenceContext();
  const handleUpload = useHandleUpload();
  const onUploadImageFile = useCallback(
    (file: File) => {
      if (codeMirror === undefined) {
        logger.error("can't upload image without codemirror reference");
        return;
      }
      return file;
    },
    [codeMirror, handleUpload]
  );

  return (
    <Fragment>
      <Button
        variant={variant}
        onClick={buttonClick}
        disabled={!codeMirror}
        title={t('editor.editorToolbar.uploadImage') ?? undefined}
        {...cypressId('editor-toolbar-upload-image-button')}>
        <UiIcon icon={icon} size={size} />
      </Button>
      <ShowIf condition={!!codeMirror}>
        <UploadInput
          onLoad={(file: File) => onUploadImage(onUploadImageFile(file))}
          allowedFileTypes={supportedFormats}
          onClickRef={clickRef}
          {...cypressId('editor-toolbar-upload-image-input')}
        />
      </ShowIf>
    </Fragment>
  );
};
